// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { escapeHtml } from "../core/html.js";
import { healthMetricLabels } from "../analysis/summary.js";
import { openAiUserFacingErrorMessage } from "./openai-error.js";
import { normalizeRotaxAiSummaryLanguage } from "./rotax-summary-language.js";
import { hasAdvisoryLanguageViolation } from "./advisory-language.js";

const AI_SUMMARY_EVIDENCE_INITIAL_LIMIT = 5;

const AI_SUMMARY_SENTENCE_ABBREVIATIONS = new Set([
  "avg",
  "approx",
  "dr",
  "e.g",
  "etc",
  "fig",
  "figs",
  "i.e",
  "max",
  "min",
  "mr",
  "mrs",
  "ms",
  "no",
  "nos",
  "st",
  "vs",
]);

const AI_SUMMARY_PHASE_LABELS = Object.freeze([
  "Startup",
  "Taxi/Runup",
  "Takeoff",
  "Climb",
  "Cruise",
  "Descent",
  "Landing",
  "Shutdown",
]);

const AI_SUMMARY_NAVIGABLE_HEALTH_KEYS = new Set([
  "sensors",
  "oilSystem",
  "coolingRate",
  "cooling",
  "combustion",
  "valve",
  "egt",
  "cht",
  "divergence",
  "descentEgtDropout",
  "inductionLeak",
  "mag",
  "carbIce",
  "charging",
  "electrical",
]);

export function aiSummaryPayloadJson(payload, summaryContext = null) {
  return normalizeRotaxAiSummaryLanguage(
    payload?.aiSummary?.summary_json ?? payload?.summary_json ?? null,
    summaryContext,
  );
}

export function aiSummaryGeneratedLabel(row) {
  const value = row?.generated_at ?? row?.summary_json?.generator?.generatedAt ?? row?.generator?.generatedAt ?? null;
  if (!value) return "";
  // SQLite CURRENT_TIMESTAMP is UTC but lacks a "Z" — browsers parse "YYYY-MM-DD HH:MM:SS"
  // as local time, mislabeling UTC as the client's zone. Force UTC so Intl renders local time.
  const isoValue = typeof value === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
    ? `${value.replace(" ", "T")}Z`
    : value;
  const date = new Date(isoValue);
  if (Number.isNaN(date.valueOf())) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function aiSummaryErrorMessage(error) {
  const message = openAiUserFacingErrorMessage(error?.message ?? error ?? "AI summary could not be generated.")
    .replace(/^Could not build AI summary:\s*/i, "");
  if (/OPENAI_API_KEY|model is not configured|rejected the configured API key/i.test(message)) {
    return "AI summaries aren't available right now. If this keeps happening, the server's AI provider needs a valid OPENAI_API_KEY, or AEROINSIGHT_AI_SUMMARY_FAKE=1 for local testing.";
  }
  return message;
}

export function aiSummaryEvidenceKeysAttr(item) {
  const keys = Array.isArray(item?.evidenceKeys) ? item.evidenceKeys : [];
  return escapeHtml(keys.filter(Boolean).join(" "));
}

export function aiSummaryClassifyItem(item) {
  const haystack = `${item?.title ?? ""} ${item?.label ?? ""} ${item?.value ?? ""} ${item?.detail ?? ""} ${item?.text ?? ""}`.toLowerCase();
  if (/\bsensor|probe|quality|channel|missing/.test(haystack)) return "sensors";
  if (/\boil|pressure|psi\b/.test(haystack)) return "oilSystem";
  if (/\bcooling rate|f\/min|cooling-rate/.test(haystack)) return "coolingRate";
  if (/\bcooling|shock cool|cool\b/.test(haystack)) return "cooling";
  if (/\bcht\/egt|egt\/cht|pairing|combustion/.test(haystack)) return "combustion";
  if (/\begt (?:rhythm|stability)|rhythm|valve/.test(haystack)) return "valve";
  if (/\begt|exhaust|balance/.test(haystack)) return "egt";
  if (/\bcht|temperature|cyl|cylinder/.test(haystack)) return "cht";
  if (/\bdivergence|split|transient/.test(haystack)) return "divergence";
  if (/\bdescent egt|dropout/.test(haystack)) return "descentEgtDropout";
  if (/\binduction|intake/.test(haystack)) return "inductionLeak";
  if (/\bmag|ignition/.test(haystack)) return "mag";
  if (/\bcarb|carburetor|ice/.test(haystack)) return "carbIce";
  if (/\bstart charge|charging|recovered/.test(haystack)) return "charging";
  if (/\bvolt|amp|electrical|alternator|battery|bus/.test(haystack)) return "electrical";
  if (/\bstable|normal|within|clear|good|overall score|no exceedance|no finding/.test(haystack)) return "normal";
  if (/\bcaveat|limit|confidence|sample/.test(haystack)) return "caution";
  return "caution";
}

export function aiSummaryIconProfile(kind) {
  return {
    sensors: ["amber", "!"],
    oilSystem: ["amber", "O"],
    cht: ["green", "♥"],
    egt: ["amber", "≋"],
    valve: ["purple", "⌁"],
    combustion: ["amber", "Δ"],
    divergence: ["teal", "⇅"],
    descentEgtDropout: ["purple", "↓"],
    inductionLeak: ["amber", "I"],
    mag: ["amber", "M"],
    carbIce: ["amber", "C"],
    cooling: ["red", "↟"],
    coolingRate: ["amber", "↡"],
    charging: ["blue", "↯"],
    electrical: ["blue", "ϟ"],
    normal: ["green", "✓"],
    caution: ["amber", "!"],
    context: ["blue", "∴"],
    sparkle: ["blue", "✦"],
    refresh: ["blue", "↻"],
  }[kind] ?? ["amber", "!"];
}

export function aiSummaryIconMarkup(kind) {
  const [className, glyph] = aiSummaryIconProfile(kind);
  return `<span class="system-icon ai-summary-card-icon ai-summary-icon-${escapeHtml(kind)} ${className}" aria-hidden="true"><span class="ai-summary-card-glyph">${escapeHtml(glyph)}</span></span>`;
}

export function aiSummaryItemHealthKey(item) {
  const kind = aiSummaryClassifyItem(item);
  return AI_SUMMARY_NAVIGABLE_HEALTH_KEYS.has(kind) ? kind : "";
}

export function aiSummaryItemSpecialTest(item) {
  const keys = Array.isArray(item?.evidenceKeys) ? item.evidenceKeys : [];
  const text = [
    item?.title,
    item?.label,
    item?.value,
    item?.detail,
    item?.text,
    ...keys,
  ].join(" ");
  return /\bGAMI\b/i.test(text)
    ? "gami-sweep"
    : "";
}

export function aiSummaryNavigationAttrs(item) {
  const specialTest = aiSummaryItemSpecialTest(item);
  if (specialTest === "gami-sweep") {
    return ' data-ai-summary-special-test="gami-sweep" tabindex="0" aria-label="Open saved GAMI sweep in Graph Explorer"';
  }
  const healthKey = aiSummaryItemHealthKey(item);
  return healthKey
    ? ` data-ai-summary-health-key="${escapeHtml(healthKey)}" tabindex="0" aria-label="Open ${escapeHtml(healthMetricLabels[healthKey] ?? healthKey)} in Graph Explorer"`
    : "";
}

export function aiSummaryBriefItemMarkup(item, index) {
  const kind = aiSummaryClassifyItem(item);
  const tone = kind === "normal" ? "good" : kind === "caution" ? "caution" : "watch";
  const label = tone === "good" ? "Normal" : tone === "caution" ? "Attention" : "Watch";
  return `
    <li class="ai-summary-standout-item ai-summary-kind-${kind}" data-evidence-keys="${aiSummaryEvidenceKeysAttr(item)}"${aiSummaryNavigationAttrs(item)}>
      ${aiSummaryIconMarkup(kind)}
      <div>
        <strong>${escapeHtml(item?.title ?? "Summary item")}</strong>
        <div class="ai-summary-standout-copy">${aiSummaryParagraphMarkup(item?.text)}</div>
      </div>
      <span class="ai-summary-status ai-summary-status-${tone}">${escapeHtml(label)}</span>
    </li>
  `;
}

export function aiSummaryBriefItemsMarkup(items = []) {
  const list = Array.isArray(items) ? items.slice(0, 4) : [];
  if (!list.length) return '<p class="ai-summary-empty">Nothing stood out on this flight.</p>';
  return `<ul class="ai-summary-standout-list">${list.map(aiSummaryBriefItemMarkup).join("")}</ul>`;
}

export function aiSummaryReviewFocusMarkup(items = []) {
  const list = Array.isArray(items) ? items.slice(0, 6) : [];
  if (!list.length) return '<p class="ai-summary-empty">No watch items for this flight.</p>';
  return list.map((item) => {
    const kind = aiSummaryClassifyItem(item);
    return `
      <article class="ai-summary-focus-item ai-summary-kind-${kind}" data-evidence-keys="${aiSummaryEvidenceKeysAttr(item)}"${aiSummaryNavigationAttrs(item)}>
        ${aiSummaryIconMarkup(kind)}
        <div>
          <strong>${escapeHtml(item?.title ?? "Review focus")}</strong>
          <div>${aiSummaryParagraphMarkup(item?.text)}</div>
        </div>
      </article>
    `;
  }).join("");
}

export function aiSummaryEvidenceRowsMarkup(items = [], expanded = false) {
  const sourceItems = Array.isArray(items) ? items : [];
  const total = sourceItems.length;
  const displayCount = expanded ? total : Math.min(AI_SUMMARY_EVIDENCE_INITIAL_LIMIT, total);
  const list = sourceItems.slice(0, displayCount);
  if (!list.length) return '<p class="ai-summary-empty">No supporting details for this flight.</p>';
  return `
    <div class="ai-summary-evidence-table" role="table" aria-label="AI summary supporting evidence">
      <div class="ai-summary-evidence-head" role="row">
        <span></span>
        <span>Topic</span>
        <span>What it means</span>
        <span>Sources</span>
      </div>
      ${list.map((item) => {
        const kind = aiSummaryClassifyItem(item);
        const sources = Array.isArray(item?.evidenceKeys) ? item.evidenceKeys.length : 0;
        return `
          <article class="ai-summary-evidence-row ai-summary-kind-${kind}" role="row" data-evidence-keys="${aiSummaryEvidenceKeysAttr(item)}"${aiSummaryNavigationAttrs(item)}>
            ${aiSummaryIconMarkup(kind)}
            <strong>${escapeHtml(item?.label ?? "Evidence")}</strong>
            <p><b>${escapeHtml(hasAdvisoryLanguageViolation(item?.value) ? "Evidence-bound item" : item?.value ?? "")}</b>${item?.detail && !hasAdvisoryLanguageViolation(item.detail) ? `<span>${escapeHtml(item.detail)}</span>` : ""}</p>
            <span class="ai-summary-source-count" title="${escapeHtml(sources || 1)} evidence reference${sources === 1 ? "" : "s"}" aria-label="${escapeHtml(sources || 1)} evidence reference${sources === 1 ? "" : "s"}">${escapeHtml(sources || 1)}</span>
          </article>
        `;
      }).join("")}
      ${total > AI_SUMMARY_EVIDENCE_INITIAL_LIMIT ? `
        <div class="ai-summary-evidence-more">
          <span>${escapeHtml(displayCount)} of ${escapeHtml(total)}</span>
          <button type="button" data-ai-summary-evidence-toggle aria-expanded="${expanded ? "true" : "false"}">
            ${expanded ? "Show less" : "Show more"}
          </button>
        </div>
      ` : ""}
    </div>
  `;
}

export function aiSummaryIsAbbreviationPeriod(value, index) {
  const prefix = value.slice(0, index + 1);
  if (/(?:\b[A-Za-z]\.){2,}$/.test(prefix)) return true;
  const token = prefix.match(/\b([A-Za-z](?:\.[A-Za-z])?|[A-Za-z]{1,10})\.$/);
  return token ? AI_SUMMARY_SENTENCE_ABBREVIATIONS.has(token[1].toLowerCase()) : false;
}

export function aiSummarySentenceEndIndex(value, index) {
  const character = value[index];
  if (!".!?".includes(character)) return -1;
  const previous = value[index - 1] ?? "";
  const next = value[index + 1] ?? "";
  if (character === "." && /\d/.test(previous) && /\d/.test(next)) return -1;
  if (character === "." && aiSummaryIsAbbreviationPeriod(value, index)) return -1;
  let end = index;
  while (/[\])}"']/.test(value[end + 1] ?? "")) end += 1;
  const following = value[end + 1] ?? "";
  return following && !/\s/.test(following) ? -1 : end;
}

export function aiSummarySentences(text) {
  const value = String(text ?? "").replace(/\s+/g, " ").trim();
  if (!value) return [];
  const sentences = [];
  let start = 0;
  for (let index = 0; index < value.length; index += 1) {
    const sentenceEnd = aiSummarySentenceEndIndex(value, index);
    if (sentenceEnd < 0) continue;
    const sentence = value.slice(start, sentenceEnd + 1).trim();
    if (sentence) sentences.push(sentence);
    index = sentenceEnd;
    while (/\s/.test(value[index + 1] ?? "")) index += 1;
    start = index + 1;
  }
  const remainder = value.slice(start).trim();
  if (remainder) sentences.push(remainder);
  // Stored legacy summaries predate the generation-time advisory guard. Keep
  // unsafe clearance or unsupported-assurance sentences out of the UI even
  // before the owner regenerates that summary.
  return sentences.filter((sentence) => !hasAdvisoryLanguageViolation(sentence));
}

export function aiSummaryEmphasizedInlineMarkup(text) {
  const value = escapeHtml(text);
  const patterns = [
    /\b(?:EGT balance(?: status| score)?(?: is)?(?: Watch)?(?: \(score \d+\)| \d+)?)\b/gi,
    /\b(?:Left Cylinder 2|L Cyl 2|Right Cylinder 5|R Cyl 5)\b/g,
    /\b(?:landing window|landing-focused pairing window|low-power\/landing|stable cruise)\b/gi,
    /\b(?:probe reference(?: is)? coherent|sensor reference(?: is)? coherent|not a broad sensor failure)\b/gi,
  ];
  return patterns.reduce(
    (html, pattern) => html.replace(pattern, (match) => `<strong>${match}</strong>`),
    value,
  );
}

export function aiSummaryParagraphMarkup(text, { emphasize = false } = {}) {
  const sentences = aiSummarySentences(text);
  const inlineMarkup = emphasize ? aiSummaryEmphasizedInlineMarkup : escapeHtml;
  return sentences.map((sentence) => `<p>${inlineMarkup(sentence)}</p>`).join("");
}

export function aiSummaryOverviewParts(text) {
  const sentences = aiSummarySentences(text);
  if (!sentences.length) return { advisory: "", body: "" };
  const first = sentences[0];
  const advisory = /not (?:a )?(?:maintenance diagnosis|airworthiness determination|permission|authorization)|evidence found/i.test(first)
    ? first
    : "";
  return {
    advisory,
    body: (advisory ? sentences.slice(1) : sentences).join(" "),
  };
}

export function aiSummaryPhaseLabel(title) {
  const value = String(title ?? "").trim();
  if (!value) return "";
  const normalized = value.toLowerCase().replace(/\s*(?:\/|&|\band\b|\+)\s*/g, "/").replace(/\s+/g, " ");
  if (/startup|start-up/.test(normalized)) return "Startup";
  if (/taxi|runup|run-up|ground roll/.test(normalized)) return "Taxi/Runup";
  if (/takeoff|take-off/.test(normalized)) return "Takeoff";
  if (/\bclimb\b/.test(normalized)) return "Climb";
  if (/\bcruise\b/.test(normalized)) return "Cruise";
  if (/\bdescent\b/.test(normalized)) return "Descent";
  if (/\blanding\b|approach/.test(normalized)) return "Landing";
  if (/shutdown|shut-down/.test(normalized)) return "Shutdown";
  return "";
}

export function aiSummaryPhaseContextItems(summary) {
  const context = Array.isArray(summary?.operatingContext) ? summary.operatingContext : [];
  const phaseItems = [];
  const seen = new Set();
  for (const item of context) {
    const label = aiSummaryPhaseLabel(item?.title);
    if (!label || seen.has(label)) continue;
    const text = String(item?.text ?? "").trim();
    if (!text) continue;
    phaseItems.push({ label, text, evidenceKeys: item?.evidenceKeys ?? [] });
    seen.add(label);
  }
  return phaseItems.sort((a, b) => {
    const ai = AI_SUMMARY_PHASE_LABELS.indexOf(a.label);
    const bi = AI_SUMMARY_PHASE_LABELS.indexOf(b.label);
    return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
  });
}

export function aiSummaryAdvisoryMarkup() {
  const fallback = "Engine-monitor evidence summary. Not a maintenance diagnosis, airworthiness determination, or permission to continue operating.";
  const text = fallback;
  return `
    <div class="ai-summary-advisory-note">
      <span class="ai-summary-advisory-label">Advisory only</span>
      <span>${escapeHtml(text)}</span>
    </div>
  `;
}

export function aiSummaryQuickReadMarkup(summary, quickReadItems = [], { includePhaseContext = true } = {}) {
  const standouts = Array.isArray(summary?.whatStandsOut) ? summary.whatStandsOut : [];
  const focus = Array.isArray(summary?.suggestedReviewFocus) ? summary.suggestedReviewFocus : [];
  const overview = aiSummaryOverviewParts(summary?.overview ?? quickReadItems[0]);
  const phaseGroups = includePhaseContext ? aiSummaryPhaseContextItems(summary) : [];
  const visiblePhaseGroups = phaseGroups.slice(0, 2);
  const hiddenPhaseGroups = phaseGroups.slice(2);
  const groups = [
    { label: "Overview", text: overview.body || summary?.overview || quickReadItems[0] },
    { label: "Primary cue", text: standouts[0]?.text ?? quickReadItems[1] },
    ...(includePhaseContext
      ? (visiblePhaseGroups.length
        ? visiblePhaseGroups
        : [{ label: "Phase context", text: standouts[1]?.text ?? quickReadItems[2] }])
      : []),
    { label: "Review next", text: focus[0]?.text },
  ].filter((item) => String(item.text ?? "").trim());

  return `
    <div class="ai-summary-narrative-list">
      ${groups.map((item) => `
        <article class="ai-summary-narrative-item" data-evidence-keys="${aiSummaryEvidenceKeysAttr(item)}">
          <span class="ai-summary-narrative-label">${escapeHtml(item.label)}</span>
          <div class="ai-summary-narrative-copy">${aiSummaryParagraphMarkup(item.text, { emphasize: true })}</div>
        </article>
      `).join("")}
      ${hiddenPhaseGroups.length ? `
        <details class="ai-summary-phase-disclosure">
          <summary>
            <span>${escapeHtml(hiddenPhaseGroups.length)} more phase note${hiddenPhaseGroups.length === 1 ? "" : "s"}</span>
            <b>Show</b>
          </summary>
          <div class="ai-summary-phase-disclosure-body">
            ${hiddenPhaseGroups.map((item) => `
              <article class="ai-summary-narrative-item" data-evidence-keys="${aiSummaryEvidenceKeysAttr(item)}">
                <span class="ai-summary-narrative-label">${escapeHtml(item.label)}</span>
                <div class="ai-summary-narrative-copy">${aiSummaryParagraphMarkup(item.text, { emphasize: true })}</div>
              </article>
            `).join("")}
          </div>
        </details>
      ` : ""}
    </div>
  `;
}

export function aiSummaryEngineSectionsMarkup(engineSummaries = []) {
  const summaries = (Array.isArray(engineSummaries) ? engineSummaries : [])
    .filter((engineSummary) => engineSummary?.engineLabel && engineSummary?.overview);
  if (summaries.length < 2) return "";
  return `
    <section class="ai-summary-engine-summaries" aria-labelledby="aiSummaryEngineSummariesTitle">
      <div class="ai-summary-engine-summaries-heading">
        <div>
          <p class="eyebrow">Engine-specific evidence</p>
          <h3 id="aiSummaryEngineSummariesTitle">Engine Summaries</h3>
        </div>
        <p>Each section contains only evidence attributed to that engine.</p>
      </div>
      <div class="ai-summary-engine-summary-grid">
        ${summaries.map((engineSummary) => `
          <article class="ai-summary-engine-summary" data-engine-id="${escapeHtml(engineSummary.engineId)}">
            <header>
              <span class="ai-summary-engine-badge" aria-hidden="true">${escapeHtml(engineSummary.engineLabel.slice(0, 1))}</span>
              <div>
                <p>Engine-specific summary</p>
                <h4>${escapeHtml(engineSummary.engineLabel)}</h4>
              </div>
            </header>
            ${aiSummaryQuickReadMarkup(engineSummary, [
              engineSummary.overview,
              ...(Array.isArray(engineSummary.whatStandsOut) ? engineSummary.whatStandsOut.map((item) => item?.text) : []),
            ])}
          </article>
        `).join("")}
      </div>
    </section>
  `;
}

export function aiSummarySharedContextMarkup(summary) {
  const engineSummaries = Array.isArray(summary?.engineSummaries) ? summary.engineSummaries : [];
  const phaseItems = aiSummaryPhaseContextItems(summary);
  if (engineSummaries.length < 2 || !phaseItems.length) return "";
  return `
    <section class="ai-summary-shared-context" aria-labelledby="aiSummarySharedContextTitle">
      <div class="ai-summary-shared-context-heading">
        <div>
          <p class="eyebrow">Aircraft-level context</p>
          <h3 id="aiSummarySharedContextTitle">Shared / Unassigned Instrumentation</h3>
        </div>
        <p>These logged channels are not attributed to one specific engine, so AlaryIQ does not assign them to an engine summary.</p>
      </div>
      <div class="ai-summary-narrative-list">
        ${phaseItems.map((item) => `
          <article class="ai-summary-narrative-item" data-evidence-keys="${aiSummaryEvidenceKeysAttr(item)}">
            <span class="ai-summary-narrative-label">${escapeHtml(item.label)}</span>
            <div class="ai-summary-narrative-copy">${aiSummaryParagraphMarkup(item.text, { emphasize: true })}</div>
          </article>
        `).join("")}
      </div>
    </section>
  `;
}

function aiSummaryElapsedClockLabel(value) {
  const seconds = Math.max(0, Math.round(Number(value) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    : `${minutes}:${String(remainder).padStart(2, "0")}`;
}

function aiSummaryTraceValue(value, unit) {
  if (!Number.isFinite(Number(value))) return "n/a";
  return `${Number(value)}${unit ? ` ${unit}` : ""}`;
}

function aiSummaryExploratoryClassificationLabel(value) {
  return {
    "normal-operating-pattern": "Normal operating pattern",
    "operational-transition": "Operational transition",
    "persistent-relationship": "Persistent relationship",
    "data-quality-limitation": "Data-quality limitation",
    "unusual-unconfirmed-pattern": "Unusual, unconfirmed",
  }[String(value ?? "").trim().toLowerCase()] ?? "Exploratory observation";
}

export function aiSummaryExploratoryAnalysisMarkup(exploratoryAnalysis) {
  if (!Array.isArray(exploratoryAnalysis) || !exploratoryAnalysis.length) return "";
  return `
    <section class="ai-summary-exploratory" aria-labelledby="aiSummaryExploratoryTitle">
      <div class="ai-summary-exploratory-heading">
        <div>
          <p class="eyebrow">Optional full-flight pattern scan</p>
          <h3 id="aiSummaryExploratoryTitle">AI Exploratory Analysis</h3>
        </div>
        <span class="ai-summary-exploratory-badge">May vary when regenerated</span>
      </div>
      <div class="ai-summary-exploratory-notice">
        ${aiSummaryIconMarkup("sparkle")}
        <p><strong>Separate from detector findings.</strong> The AI looked across a compressed trace of the complete normalized engine log. AlaryIQ verified every displayed engine, cylinder, channel, phase, time window, and value against the original samples, but the pattern interpretation remains exploratory.</p>
      </div>
      <div class="ai-summary-exploratory-list">
        ${exploratoryAnalysis.map((item) => {
          const start = aiSummaryElapsedClockLabel(item?.startElapsedSeconds);
          const end = aiSummaryElapsedClockLabel(item?.endElapsedSeconds);
          const channels = (Array.isArray(item?.channelKeys) ? item.channelKeys : []).filter(Boolean).join(" ");
          const unusual = item?.classification === "unusual-unconfirmed-pattern";
          return `
            <article class="ai-summary-exploratory-item">
              <header>
                <span class="ai-summary-exploratory-scope">${escapeHtml(item?.engineLabel ?? "Shared / Unassigned")}</span>
                <span>${escapeHtml(item?.phase ?? "Flight")} · ${escapeHtml(start)}–${escapeHtml(end)}</span>
              </header>
              <span class="ai-summary-exploratory-classification" data-classification="${escapeHtml(item?.classification ?? "")}">${escapeHtml(aiSummaryExploratoryClassificationLabel(item?.classification))}</span>
              <h4>${escapeHtml(item?.title ?? "Exploratory observation")}</h4>
              <div class="ai-summary-exploratory-copy">${aiSummaryParagraphMarkup(item?.observation)}</div>
              ${unusual ? `<p class="ai-summary-exploratory-unconfirmed">Visible in the trace, but not flagged by the deterministic analysis.</p>` : ""}
              <div class="ai-summary-exploratory-traces" aria-label="Verified telemetry values">
                ${(Array.isArray(item?.trace) ? item.trace : []).map((trace) => `
                  <span title="Verified source channel ${escapeHtml(trace?.channelKey ?? "")}">
                    <b>${escapeHtml(trace?.label ?? trace?.channelKey ?? "Channel")}</b>
                    ${escapeHtml(aiSummaryTraceValue(trace?.start, trace?.unit))} → ${escapeHtml(aiSummaryTraceValue(trace?.end, trace?.unit))}
                    <small>range ${escapeHtml(aiSummaryTraceValue(trace?.min, trace?.unit))}–${escapeHtml(aiSummaryTraceValue(trace?.max, trace?.unit))}</small>
                  </span>
                `).join("")}
              </div>
              <button
                class="secondary-action ai-summary-exploratory-open"
                type="button"
                data-ai-summary-exploratory-open
                data-ai-summary-channel-keys="${escapeHtml(channels)}"
                data-ai-summary-engine-id="${escapeHtml(item?.engineId ?? "")}"
                data-ai-summary-start-time="${escapeHtml(item?.startElapsedSeconds ?? "")}"
                data-ai-summary-end-time="${escapeHtml(item?.endElapsedSeconds ?? "")}"
              >Open this window in Graph Explorer</button>
            </article>
          `;
        }).join("")}
      </div>
    </section>
  `;
}

export function aiSummaryDisclosureItemsMarkup(items = [], emptyText = "No items returned.") {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return `<div class="ai-summary-disclosure-body"><p>${escapeHtml(emptyText)}</p></div>`;
  return `
    <div class="ai-summary-disclosure-body">
      ${list.map((item) => `
        <article class="ai-summary-disclosure-item" data-evidence-keys="${aiSummaryEvidenceKeysAttr(item)}">
          ${item?.title ? `<h4>${escapeHtml(item.title)}</h4>` : ""}
          <div>${aiSummaryParagraphMarkup(item?.text)}</div>
        </article>
      `).join("")}
    </div>
  `;
}

export function aiSummaryGraphMarkup(spec) {
  if (!spec) return "";
  return `
    <figure class="ai-summary-evidence-graph" data-ai-summary-graph-card="${escapeHtml(spec.id)}">
      <figcaption>
        <span>
          <strong>${escapeHtml(spec.title)}</strong>
          ${spec.subtitle ? `<small>${escapeHtml(spec.subtitle)}</small>` : ""}
        </span>
        ${spec.healthKey ? `<button class="secondary-action ai-summary-graph-open" type="button" data-ai-summary-graph-open="${escapeHtml(spec.healthKey)}">Open in Graph Explorer</button>` : ""}
      </figcaption>
      <canvas data-ai-summary-graph="${escapeHtml(spec.id)}" aria-label="${escapeHtml(spec.title)}"></canvas>
    </figure>
  `;
}

export function aiSummaryGraphEvidenceText(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((item) => [
      item?.title,
      item?.label,
      item?.value,
      item?.detail,
      item?.text,
      ...(Array.isArray(item?.evidenceKeys) ? item.evidenceKeys : []),
    ].filter(Boolean).join(" "))
    .join(" ");
}

export function aiSummaryEvidenceHealthKey(evidenceKeys = [], text = "") {
  const haystack = [
    ...(Array.isArray(evidenceKeys) ? evidenceKeys : []),
    text,
  ].join(" ").toLowerCase();
  const aliases = [
    ["descentEgtDropout", ["descentegtdropout", "egt dropout", "dropout"]],
    ["fuelFlowResponse", ["fuelflowresponse", "fuel-flow-response", "fuel flow response"]],
    ["fuelFlowWorkload", ["fuelflowworkload", "fuel-flow-workload", "fuel flow workload", "workload detector"]],
    ["ropLeaningContext", ["ropleaningcontext", "rop-leaning-context", "rop leaning", "rich of peak", "rich-of-peak"]],
    ["lopLeaningContext", ["lopleaningcontext", "lop-leaning-context", "lop leaning", "lean of peak", "lean-of-peak"]],
    ["powerChangeContext", ["powerchangecontext", "power-change-context", "power change"]],
    ["cylinderHarmony", ["cylinderharmony", "cylinder-harmony", "cylinder harmony", "harmonious cylinder"]],
    ["chtRhythm", ["chtrhythm", "cht-rhythm", "cht rhythm", "cht-only rhythm", "localized cht rhythm"]],
    ["cylinderStartupSymmetry", ["cylinderstartupsymmetry", "cylinder-startup-symmetry", "cylinder startup symmetry", "startup symmetry", "warmup symmetry", "lightoff symmetry"]],
    ["patternWorkContext", ["patternworkcontext", "pattern-work-context", "pattern work", "pattern-work"]],
    ["thermalContext", ["thermalcontext", "thermal-context", "thermal context"]],
    ["sensorCoherence", ["sensorcoherence", "sensor-coherence", "sensor coherence"]],
    ["oilSystem", ["oilsystem", "oil-system", "oil pressure", "oil temperature"]],
    ["coolingRate", ["coolingrate", "cooling-rate", "cooling rate"]],
    ["engineDelta", ["enginedelta", "engine-delta", "engine delta"]],
    ["inductionLeak", ["inductionleak", "induction-leak", "induction leak"]],
    ["carbIce", ["carbice", "carb-ice", "carb ice"]],
    ["charging", ["start charge", "charging"]],
    ["electrical", ["electrical", "bus voltage", "buss"]],
    ["sensors", ["sensor", "probe", "instrumentation"]],
    ["valve", ["valve", "egt rhythm", "egt stability", "rhythm"]],
    ["combustion", ["combustion", "cht/egt", "pairing"]],
    ["divergence", ["divergence"]],
    ["cooling", ["cooling"]],
    ["turbo", ["turbo", "tit", "upper deck"]],
    ["mag", ["mag check", "mag"]],
    ["cht", ["cht"]],
    ["egt", ["egt balance", "egt spread", "egt"]],
  ];
  if (/\begt\s+(?:stability|rhythm)\b/.test(haystack)) return "valve";
  const exact = Object.keys(healthMetricLabels).find((key) => haystack.includes(key.toLowerCase()));
  if (exact) return exact;
  return aliases.find(([, values]) => values.some((value) => haystack.includes(value)))?.[0] ?? null;
}
